import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Search, Edit2, Save, X, Upload, Download, AlertCircle, Loader, Loader2, ArrowUp, ArrowDown, CheckSquare, Package, Tags } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import UserLayout from './UserLayout';
import PageHeader from '../shared/PageHeader';
import Card from '../shared/Card';
import Button from '../shared/Button';
import EmptyState from '../shared/EmptyState';
import { showNotification, showError } from '../../utils/notificationHandler';
import * as XLSX from 'xlsx';

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent';

const LoadingOverlay = ({ message }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
      <Loader className="w-12 h-12 text-brand animate-spin" />
      <p className="mt-4 text-lg font-semibold text-gray-900">{message}</p>
    </div>
  </div>
);

const ConfirmationDialog = ({ isOpen, onClose, onConfirm, message }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="confirm-dialog-message"
          className="bg-white p-6 rounded-lg shadow-xl max-w-md mx-4"
        >
          <p id="confirm-dialog-message" className="text-lg font-semibold mb-4 text-gray-900">{message}</p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="danger" onClick={onConfirm}>
              Delete
            </Button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const SortButton = ({ field, label, sortField, sortDirection, onSort }) => (
  <th 
    scope="col" 
    className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200"
    onClick={() => onSort(field)}
  >
    <div className="flex items-center">
      {label}
      {sortField === field && (
        sortDirection === 'asc' ? <ArrowUp className="ml-1 h-4 w-4" /> : <ArrowDown className="ml-1 h-4 w-4" />
      )}
    </div>
  </th>
);

const UserPartNumberManagement = () => {
  const [loading, setLoading] = useState(false);
  const [customerPartNumber, setCustomerPartNumber] = useState('');
  const [customerDescription, setCustomerDescription] = useState('');
  const [mhsPartNumber, setMhsPartNumber] = useState('');
  const [mhsDescription, setMhsDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [mappings, setMappings] = useState([]);
  const [userId, setUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedMappings, setSelectedMappings] = useState([]);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const placeholderImageUrl = "https://syd1.digitaloceanspaces.com/cdn-assets.mhs/images/placeholders/mhs_placeholder.png";

  useEffect(() => {
    fetchUserData();
    fetchMappings();
  }, []);

  const fetchUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      showError(error, 'Failed to fetch user data');
    }
  };

  const fetchMappings = async () => {
    try {
      const { data, error } = await supabase
        .from('part_number_mappings')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMappings(data);
    } catch (error) {
      console.error('Error fetching mappings:', error);
      showError(error, 'Failed to fetch part number mappings');
    }
  };

  const handleMhsPartNumberSearch = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/enquire/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ partNumber: mhsPartNumber }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.Description) {
        setMhsDescription(data.Description);
        setImageUrl(data.small_image_url || placeholderImageUrl);
        showNotification('MHS part number found', 'success');
      } else {
        showNotification('MHS part number not found', 'error');
        setMhsDescription('');
        setImageUrl('');
      }
    } catch (error) {
      console.error('Error searching MHS part number:', error);
      showError(error, 'Failed to search MHS part number');
      setMhsDescription('');
      setImageUrl('');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!mhsDescription) {
      showNotification('Please search for a valid MHS part number first', 'error');
      return;
    }
    try {
      const { data, error } = await supabase
        .from('part_number_mappings')
        .insert([
          {
            user_id: userId,
            customer_part_number: customerPartNumber,
            customer_description: customerDescription,
            mhs_part_number: mhsPartNumber,
            mhs_description: mhsDescription,
            image_url: imageUrl,
          }
        ]);

      if (error) throw error;
      showNotification('Part number mapping added successfully', 'success');
      setCustomerPartNumber('');
      setCustomerDescription('');
      setMhsPartNumber('');
      setMhsDescription('');
      setImageUrl('');
      fetchMappings();
    } catch (error) {
      console.error('Error adding mapping:', error);
      showError(error, 'Failed to add part number mapping');
    }
  };

  const handleDelete = async (id) => {
    setConfirmAction(() => async () => {
      try {
        const { error } = await supabase
          .from('part_number_mappings')
          .delete()
          .eq('id', id);

        if (error) throw error;
        showNotification('Part number mapping deleted successfully', 'success');
        fetchMappings();
      } catch (error) {
        console.error('Error deleting mapping:', error);
        showError(error, 'Failed to delete part number mapping');
      }
    });
    setIsConfirmDialogOpen(true);
  };

  const handleEdit = (mapping) => {
    setEditingId(mapping.id);
    setEditForm({ ...mapping });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSaveEdit = async () => {
    try {
      const { error } = await supabase
        .from('part_number_mappings')
        .update(editForm)
        .eq('id', editingId);

      if (error) throw error;
      showNotification('Part number mapping updated successfully', 'success');
      setEditingId(null);
      setEditForm({});
      fetchMappings();
    } catch (error) {
      console.error('Error updating mapping:', error);
      showError(error, 'Failed to update part number mapping');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && (selectedFile.type === 'text/csv' || selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
      setFile(selectedFile);
    } else {
      showNotification('Please select a valid CSV or Excel file', 'error');
    }
  };

  const handleBulkUpload = async () => {
    if (!file) {
      showNotification('Please select a file to upload', 'error');
      return;
    }

    setUploading(true);
    setIsSearching(true);
    try {
      const fileData = await readFileAsync(file);
      const mappings = parseFileData(fileData);
      await processAndValidateMappings(mappings);
      showNotification('Bulk upload completed successfully', 'success');
      setFile(null);
      fetchMappings();
    } catch (error) {
      showError(error, 'Failed to process the upload');
    } finally {
      setUploading(false);
      setIsSearching(false);
    }
  };

  const readFileAsync = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsBinaryString(file);
    });
  };

  const parseFileData = (data) => {
    const workbook = XLSX.read(data, { type: 'binary' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    return XLSX.utils.sheet_to_json(sheet);
  };

  const processAndValidateMappings = async (mappings) => {
    for (const mapping of mappings) {
      if (!mapping.CustomerPartNumber || !mapping.MHSPartNumber) {
        throw new Error('Invalid mapping: Customer Part Number and MHS Part Number are required');
      }

      // Validate MHS Part Number
      const response = await fetch('/api/enquire/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ partNumber: mapping.MHSPartNumber }),
      });

      if (!response.ok) {
        throw new Error(`Invalid MHS Part Number: ${mapping.MHSPartNumber}`);
      }

      const data = await response.json();

      // If valid, add to database
      const { error } = await supabase.from('part_number_mappings').insert({
        user_id: userId,
        customer_part_number: mapping.CustomerPartNumber,
        mhs_part_number: mapping.MHSPartNumber,
        customer_description: mapping.CustomerDescription || '',
        mhs_description: data.Description || '',
        image_url: data.small_image_url || placeholderImageUrl,
      });

      if (error) throw error;
    }
  };

  const downloadTemplate = (format) => {
    const data = [
      { CustomerPartNumber: 'CUST001', MHSPartNumber: 'MHS001', CustomerDescription: 'Custom Hydraulic Pump' },
      { CustomerPartNumber: 'CUST002', MHSPartNumber: 'MHS002', CustomerDescription: 'Industrial Valve Assembly' },
    ];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Mappings");

    if (format === 'xlsx') {
      XLSX.writeFile(wb, "part_number_mapping_template.xlsx");
    } else if (format === 'csv') {
      XLSX.writeFile(wb, "part_number_mapping_template.csv", { bookType: "csv" });
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = () => {
    const dataToExport = mappings.map(mapping => ({
      'Customer Part Number': mapping.customer_part_number,
      'Customer Description': mapping.customer_description,
      'MHS Part Number': mapping.mhs_part_number,
      'MHS Description': mapping.mhs_description,
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Mappings");
    XLSX.writeFile(wb, "part_number_mappings_export.xlsx");
  };

  const handleSelectMapping = (id) => {
    setSelectedMappings(prev => 
      prev.includes(id) ? prev.filter(mappingId => mappingId !== id) : [...prev, id]
    );
  };

  const handleBatchDelete = () => {
    if (selectedMappings.length === 0) {
      showNotification('Please select mappings to delete', 'error');
      return;
    }

    setConfirmAction(() => async () => {
      try {
        const { error } = await supabase
          .from('part_number_mappings')
          .delete()
          .in('id', selectedMappings);

        if (error) throw error;
        showNotification('Selected mappings deleted successfully', 'success');
        setSelectedMappings([]);
        fetchMappings();
      } catch (error) {
        console.error('Error deleting mappings:', error);
        showError(error, 'Failed to delete selected mappings');
      }
    });
    setIsConfirmDialogOpen(true);
  };

  const filteredMappings = mappings.filter(mapping =>
    mapping.customer_part_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    mapping.mhs_part_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedMappings = [...filteredMappings].sort((a, b) => {
    if (a[sortField] < b[sortField]) return sortDirection === 'asc' ? -1 : 1;
    if (a[sortField] > b[sortField]) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedMappings.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(sortedMappings.length / itemsPerPage);

  return (
    <UserLayout>
      <div className="space-y-6">
        <PageHeader
          title="Part Number Management"
          description="Map your own part numbers to MHS part numbers so you can search and order using either."
        />

        {/* Bulk Upload Section */}
        <Card header={<h2 className="text-lg font-semibold text-gray-900">Bulk Part Number Upload</h2>}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Upload File</h3>
              <div className="space-y-4">
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  id="file-upload"
                />
                {/* Styled to match Button variant="secondary"; a <label> keeps the native file picker */}
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors duration-200"
                >
                  <Upload className="w-4 h-4" aria-hidden="true" />
                  <span>{file ? 'Change File' : 'Choose File'}</span>
                </label>

                {file && (
                  <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-3 min-w-0">
                      <Package className="w-5 h-5 text-gray-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-700 truncate">{file.name}</span>
                      <span className="text-xs text-gray-500 flex-shrink-0">({(file.size / 1024).toFixed(1)} KB)</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFile(null)}
                      ariaLabel="Remove file"
                      icon={<X className="w-4 h-4" aria-hidden="true" />}
                    />
                  </div>
                )}

                <Button
                  onClick={handleBulkUpload}
                  disabled={!file}
                  loading={uploading}
                  icon={<Upload className="w-4 h-4" aria-hidden="true" />}
                >
                  {uploading ? 'Processing...' : 'Upload and Process'}
                </Button>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Download Templates</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="secondary"
                  onClick={() => downloadTemplate('xlsx')}
                  icon={<Download className="w-4 h-4" aria-hidden="true" />}
                >
                  Excel Template
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => downloadTemplate('csv')}
                  icon={<Download className="w-4 h-4" aria-hidden="true" />}
                >
                  CSV Template
                </Button>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="flex-shrink-0 h-5 w-5 text-blue-600 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Upload Requirements:</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <ul className="list-disc pl-5 space-y-1">
                    <li>File must be in CSV or Excel format</li>
                    <li>Required columns: CustomerPartNumber, MHSPartNumber</li>
                    <li>Optional column: CustomerDescription</li>
                    <li>MHS Part Numbers will be validated against our system</li>
                    <li>Invalid MHS Part Numbers will cause the upload to fail</li>
                    <li>Existing mappings will be overwritten</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Individual Mapping Section */}
        <Card header={<h2 className="text-lg font-semibold text-gray-900">Add Individual Mapping</h2>}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="customerPartNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Part Number
                </label>
                <input
                  type="text"
                  name="customerPartNumber"
                  id="customerPartNumber"
                  value={customerPartNumber}
                  onChange={(e) => setCustomerPartNumber(e.target.value)}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label htmlFor="customerDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Description
                </label>
                <input
                  type="text"
                  name="customerDescription"
                  id="customerDescription"
                  value={customerDescription}
                  onChange={(e) => setCustomerDescription(e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label htmlFor="mhsPartNumber" className="block text-sm font-medium text-gray-700 mb-1">
                MHS Part Number
              </label>
              <div className="flex rounded-lg shadow-sm">
                <input
                  type="text"
                  name="mhsPartNumber"
                  id="mhsPartNumber"
                  value={mhsPartNumber}
                  onChange={(e) => setMhsPartNumber(e.target.value)}
                  className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                  required
                />
                <button
                  type="button"
                  onClick={handleMhsPartNumberSearch}
                  disabled={loading}
                  aria-label="Look up MHS part number"
                  className="px-4 py-2 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 rounded-r-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand transition-colors duration-200"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Search className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            {mhsDescription && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start space-x-4">
                  {imageUrl && (
                    <img src={imageUrl} alt="MHS Part" className="w-16 h-16 object-contain flex-shrink-0" />
                  )}
                  <div>
                    <h4 className="font-medium text-green-800">Part Found</h4>
                    <p className="text-sm text-green-700 mt-1">{mhsDescription}</p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <Button type="submit" icon={<Plus className="h-4 w-4" aria-hidden="true" />}>
                Add Mapping
              </Button>
            </div>
          </form>
        </Card>

        {/* Mappings Table Section */}
        <Card
          padding="none"
          header={
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Your Part Number Mappings</h2>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  onClick={handleExport}
                  icon={<Download className="h-4 w-4" aria-hidden="true" />}
                >
                  Export
                </Button>
                <Button
                  variant="danger"
                  onClick={handleBatchDelete}
                  disabled={selectedMappings.length === 0}
                  icon={<Trash2 className="h-4 w-4" aria-hidden="true" />}
                >
                  Delete Selected
                </Button>
              </div>
            </div>
          }
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="relative w-full md:w-1/2">
              <label htmlFor="mapping-search" className="sr-only">Search mappings</label>
              <input
                id="mapping-search"
                type="text"
                placeholder="Search mappings..."
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
          </div>

          {currentItems.length === 0 ? (
            <EmptyState
              icon={<Tags className="h-12 w-12" />}
              title={searchTerm ? 'No mappings match your search' : 'No mappings yet'}
              description={searchTerm
                ? 'Try a different customer or MHS part number.'
                : 'Add a mapping above, or upload a file to add many at once.'}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          aria-label="Select all mappings on this page"
                          checked={selectedMappings.length === currentItems.length && currentItems.length > 0}
                          onChange={() => {
                            if (selectedMappings.length === currentItems.length) {
                              setSelectedMappings([]);
                            } else {
                              setSelectedMappings(currentItems.map(item => item.id));
                            }
                          }}
                          className="form-checkbox h-4 w-4 text-brand rounded border-gray-300 focus:ring-brand"
                        />
                      </th>
                      <SortButton
                        field="customer_part_number"
                        label="Customer Part Number"
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                      <SortButton
                        field="customer_description"
                        label="Customer Description"
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                      <SortButton
                        field="mhs_part_number"
                        label="MHS Part Number"
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                      <SortButton
                        field="mhs_description"
                        label="MHS Description"
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                      <th scope="col" className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                      <th scope="col" className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentItems.map((mapping) => (
                      <tr key={mapping.id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            aria-label={`Select ${mapping.customer_part_number}`}
                            checked={selectedMappings.includes(mapping.id)}
                            onChange={() => handleSelectMapping(mapping.id)}
                            className="form-checkbox h-4 w-4 text-brand rounded border-gray-300 focus:ring-brand"
                          />
                        </td>
                        {editingId === mapping.id ? (
                          <>
                            <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                              <input
                                type="text"
                                aria-label="Customer part number"
                                value={editForm.customer_part_number}
                                onChange={(e) => setEditForm({ ...editForm, customer_part_number: e.target.value })}
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                              <input
                                type="text"
                                aria-label="Customer description"
                                value={editForm.customer_description}
                                onChange={(e) => setEditForm({ ...editForm, customer_description: e.target.value })}
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                              <input
                                type="text"
                                aria-label="MHS part number"
                                value={editForm.mhs_part_number}
                                onChange={(e) => setEditForm({ ...editForm, mhs_part_number: e.target.value })}
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                              <input
                                type="text"
                                aria-label="MHS description"
                                value={editForm.mhs_description}
                                onChange={(e) => setEditForm({ ...editForm, mhs_description: e.target.value })}
                                className={inputClass}
                              />
                            </td>
                            <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                              {editForm.image_url && (
                                <img src={editForm.image_url} alt="Part" className="w-12 h-12 object-contain" />
                              )}
                            </td>
                            <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  onClick={handleSaveEdit}
                                  ariaLabel="Save changes"
                                  icon={<Save className="h-4 w-4" aria-hidden="true" />}
                                />
                                <Button
                                  variant="ghost"
                                  onClick={handleCancelEdit}
                                  ariaLabel="Cancel edit"
                                  icon={<X className="h-4 w-4" aria-hidden="true" />}
                                />
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {mapping.customer_part_number}
                            </td>
                            <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {mapping.customer_description}
                            </td>
                            <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {mapping.mhs_part_number}
                            </td>
                            <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {mapping.mhs_description}
                            </td>
                            <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                              {mapping.image_url && (
                                <img src={mapping.image_url} alt="Part" className="w-12 h-12 object-contain" />
                              )}
                            </td>
                            <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  onClick={() => handleEdit(mapping)}
                                  ariaLabel={`Edit mapping ${mapping.customer_part_number}`}
                                  icon={<Edit2 className="h-4 w-4" aria-hidden="true" />}
                                />
                                <Button
                                  variant="ghostDanger"
                                  onClick={() => handleDelete(mapping.id)}
                                  ariaLabel={`Delete mapping ${mapping.customer_part_number}`}
                                  icon={<Trash2 className="h-4 w-4" aria-hidden="true" />}
                                />
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <nav className="px-6 py-4 border-t border-gray-200 flex justify-center" aria-label="Pagination">
                  <div className="flex flex-wrap gap-1">
                    {Array.from({ length: totalPages }, (_, i) => (
                      <Button
                        key={i}
                        size="sm"
                        variant={currentPage === i + 1 ? 'primary' : 'secondary'}
                        onClick={() => paginate(i + 1)}
                        aria-current={currentPage === i + 1 ? 'page' : undefined}
                      >
                        {i + 1}
                      </Button>
                    ))}
                  </div>
                </nav>
              )}
            </>
          )}
        </Card>

        {/* Processing Overlays */}
        <AnimatePresence>
          {isSearching && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <LoadingOverlay message="Processing part numbers..." />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={isConfirmDialogOpen}
          onClose={() => setIsConfirmDialogOpen(false)}
          onConfirm={() => {
            confirmAction();
            setIsConfirmDialogOpen(false);
          }}
          message="Are you sure you want to delete the selected mapping(s)?"
        />
      </div>
    </UserLayout>
  );
};

export default UserPartNumberManagement;
